import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supplyApi, profileApi, type SupplyItem } from '@/services/api';
import { fieldLabels, formatFieldValue, type GreenCertField } from '@/components/feature/GreenCertFieldConfig';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MessageModal from '@/components/feature/MessageModal';
import { useMessages } from '@/hooks/useMessages';
import { useAuth } from '@/context/AuthContext';

const DETAIL_FIELDS: GreenCertField[] = [
  'direction',
  'year',
  'halfYear',
  'month',
  'projectType',
  'channel',
  'provinceRequirement',
  'subsidy',
  'tradeMethod',
  'exchange',
  'offerBy',
  'tradePrinciple',
];

export default function SupplyDetail() {
  const { id } = useParams<{ id: string }>();
  const [showContact, setShowContact] = useState(false);
  const [item, setItem] = useState<SupplyItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messageOpen, setMessageOpen] = useState(false);
  const [favId, setFavId] = useState<number | null>(null);
  const [favBusy, setFavBusy] = useState(false);
  const { sendMessage } = useMessages();
  const { user } = useAuth();

  useEffect(() => {
    if (!id || !user) { setFavId(null); return; }
    profileApi.favorites()
      .then((list) => {
        const f = list.find((x) => x.type === 'supply' && x.itemId === Number(id));
        setFavId(f ? f.id : null);
      })
      .catch(() => {});
  }, [id, user]);

  const toggleFavorite = async () => {
    if (!user) { window.location.href = '/login'; return; }
    if (!id || favBusy) return;
    setFavBusy(true);
    try {
      if (favId != null) {
        await profileApi.removeFavorite(favId);
        setFavId(null);
      } else {
        await profileApi.addFavorite('supply', Number(id));
        const list = await profileApi.favorites();
        const f = list.find((x) => x.type === 'supply' && x.itemId === Number(id));
        setFavId(f ? f.id : null);
      }
    } catch { /* ignore */ }
    setFavBusy(false);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    supplyApi
      .detail(Number(id))
      .then((data) => {
        setItem(data);
        setLoading(false);
      })
      .catch(() => {
        setError('该供给信息不存在');
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="w-full bg-background-50 min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="w-full bg-background-50 min-h-screen flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-20 h-20 mx-auto rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="font-heading text-xl font-bold text-foreground-900 mt-4">信息不存在</h2>
          <p className="text-foreground-500 text-sm mt-2">{error || '该供给信息可能已被下架或删除'}</p>
          <Link
            to="/supply"
            className="inline-block mt-6 px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回供给列表
          </Link>
        </div>
      </div>
    );
  }

  const certTypeLabel = formatFieldValue('projectType', item.projectType);

  return (
    <>
      <Navbar />
      <div className="w-full bg-background-50 min-h-screen">
        {/* Breadcrumb header */}
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-2 text-sm text-foreground-400 mb-4">
              <Link to="/" className="hover:text-foreground-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/supply" className="hover:text-foreground-600 transition-colors cursor-pointer">绿证供给</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600">{item.company}</span>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {item.company}
              </h1>
              <span className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md">
                {certTypeLabel}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-map-pin-line text-xs"></i>
                {item.region}
              </span>
              <span className="text-foreground-400 text-xs">|</span>
              <span>发布于 {item.publishDate}</span>
              <span className="text-foreground-400 text-xs">|</span>
              {item.status === 'active' ? (
                <span className="text-primary-600 font-medium">挂牌中</span>
              ) : (
                <span className="text-foreground-400">已过期</span>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              {/* Key stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-background-100 rounded-lg p-4">
                  <p className="text-foreground-400 text-xs mb-1">绿证数量</p>
                  <p className="text-foreground-900 text-xl font-bold font-heading">
                    {item.quantity.toLocaleString()}
                  </p>
                  <p className="text-foreground-400 text-xs">个</p>
                </div>
                <div className="bg-background-100 rounded-lg p-4">
                  <p className="text-foreground-400 text-xs mb-1">参考报价</p>
                  <p className="text-foreground-900 text-xl font-bold font-heading">
                    &yen;{item.price}
                  </p>
                  <p className="text-foreground-400 text-xs">元/个</p>
                </div>
                <div className="bg-background-100 rounded-lg p-4">
                  <p className="text-foreground-400 text-xs mb-1">有效期至</p>
                  <p className="text-foreground-900 text-xl font-bold font-heading">
                    {item.expiryDate}
                  </p>
                  <p className="text-foreground-400 text-xs">
                    {item.status === 'active' ? (
                      <span className="text-primary-600">挂牌中</span>
                    ) : (
                      <span className="text-foreground-400">已过期</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Transaction details */}
              <div className="mb-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                  交易详情
                </h3>
                <div className="bg-background-100 rounded-lg p-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    {DETAIL_FIELDS.map((field) => {
                      const raw = item[field as keyof SupplyItem];
                      if (raw === undefined || raw === null || (Array.isArray(raw) && raw.length === 0)) return null;
                      const val = formatFieldValue(field, raw as string | string[]);
                      return (
                        <div key={field} className="flex items-start gap-3">
                          <span className="text-foreground-400 text-xs flex-shrink-0 w-18">
                            {fieldLabels[field]}
                          </span>
                          <span className="text-foreground-800 text-sm">{val}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                  详细说明
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed whitespace-pre-line">
                  {item.description}
                </p>
              </div>

              {/* Other info */}
              {item.otherInfo && (
                <div className="mb-8">
                  <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                    其他信息
                  </h3>
                  <div className="bg-background-100 rounded-lg p-4">
                    <p className="text-foreground-600 text-sm leading-relaxed whitespace-pre-line">
                      {item.otherInfo}
                    </p>
                  </div>
                </div>
              )}

              {/* Verification doc */}
              <div className="mb-8">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-3">
                  佐证材料
                </h3>
                <div className="flex items-center gap-3 bg-background-100 rounded-lg p-4">
                  <div className="w-10 h-10 rounded bg-primary-100 flex items-center justify-center">
                    <i className="ri-file-text-line text-primary-600"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-foreground-700 text-sm font-medium">
                      {(item as Record<string, unknown>).verificationDoc as string || '核发证明'}
                    </p>
                    <p className="text-foreground-400 text-xs mt-0.5">
                      平台不核验真伪，请自行甄别
                    </p>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="border border-accent-200 bg-accent-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex-shrink-0 mt-0.5">
                    <i className="ri-error-warning-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-accent-800 text-sm font-medium">交易风险提示</p>
                    <p className="text-accent-700 text-xs mt-1 leading-relaxed">
                      本平台为纯免费信息撮合平台，不参与交易、资金结算及权属过户。信息真实性由发布方负责，请务必自行核验对方资质与绿证真伪后再进行交易。如发现虚假信息，请及时举报。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-background-100 rounded-lg p-6 sticky top-24">
                <div className="text-center mb-5">
                  <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-building-4-line text-2xl text-primary-600"></i>
                  </div>
                  <h4 className="font-heading text-base font-bold text-foreground-900 mt-3">
                    {item.company}
                  </h4>
                  <p className="text-foreground-400 text-xs mt-1">
                    {item.region} · 供给方
                  </p>
                  {((item as Record<string, unknown>).views as number) !== undefined && (
                    <div className="flex items-center justify-center gap-4 mt-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {(item as Record<string, unknown>).views as number}
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-400">对接方式</span>
                    <span className="text-foreground-700">
                      {(item as Record<string, unknown>).contactMethod as string || '电话联系'}
                    </span>
                  </div>
                </div>

                {!showContact ? (
                  <button
                    onClick={() => setShowContact(true)}
                    className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看联系方式
                  </button>
                ) : (
                  <div className="space-y-3 p-4 bg-background-50 rounded-md border border-background-200">
                    <div>
                      <p className="text-foreground-400 text-xs">联系人</p>
                      <p className="text-foreground-900 text-sm font-medium">{item.contactPerson}</p>
                    </div>
                    <div>
                      <p className="text-foreground-400 text-xs">联系电话</p>
                      <p className="text-foreground-900 text-sm font-medium">{item.contactPhone}</p>
                    </div>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-background-200 space-y-3">
                  <button
                    onClick={() => setMessageOpen(true)}
                    className="w-full py-2.5 border border-primary-400 text-primary-600 text-sm font-medium rounded-md hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-chat-3-line mr-1.5"></i>
                    发站内信
                  </button>
                  <button
                    onClick={toggleFavorite}
                    disabled={favBusy}
                    className={`w-full py-2 border text-sm font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                      favId != null
                        ? 'border-primary-400 text-primary-600 bg-primary-50'
                        : 'border-background-300 text-foreground-600 hover:bg-background-50'
                    }`}
                  >
                    <i className={`mr-1.5 ${favId != null ? 'ri-bookmark-fill' : 'ri-bookmark-line'}`}></i>
                    {favId != null ? '已收藏' : '收藏信息'}
                  </button>
                  <button className="w-full py-2 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-alert-line mr-1.5"></i>
                    举报信息
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <MessageModal
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        listingId={item.id}
        listingType="supply"
        listingTitle={`${item.company} - ${certTypeLabel}`}
        receiverName={item.company}
        receiverId={item.userId ?? 0}
        onSend={(content) => {
          if (!item.userId) return;
          sendMessage(item.userId, {
            listingId: item.id,
            listingType: 'supply',
            listingTitle: `${item.company} - ${certTypeLabel}`,
            receiverId: item.userId,
            receiverName: item.company,
            content,
          });
        }}
      />
    </>
  );
}